import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Ban,
  Bike,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Package,
  RefreshCw,
  Search,
  Store,
  TriangleAlert,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { orderStatusLabel, OrderType, WS_EVENT } from '@kick/types';
import { getErrorMessage } from '@/lib/api';
import { usePuede } from '@/lib/permisos';
import { cn, coincide, desdeHace, formatCOP, formatDistance } from '@/lib/cn';
import {
  clavesAdmin,
  useDisponibles,
  useIntervenirPedido,
  useOperacion,
  type PedidoOperacion,
} from '@/lib/queries';
import { useConexion, useEventoSocket, type EstadoConexion } from '@/lib/socket';
import { Boton, Cargando, Entrada, ErrorCarga, Modal, Seleccion, Vacio } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

/**
 * Cuántos pedidos por página.
 *
 * Quince caben en una pantalla sin desplazar mucho y dejan sitio para las
 * métricas de arriba, que son la razón de entrar aquí. Más filas convierten
 * el monitor en una lista que hay que recorrer.
 */
const POR_PAGINA = 15;

const ALERTAS: Record<string, { texto: string; color: string }> = {
  SIN_ACEPTAR: { texto: 'El comercio no lo acepta', color: 'bg-amber-100 text-amber-900' },
  SIN_DOMICILIARIO: { texto: 'Sin domiciliario', color: 'bg-red-100 text-red-900' },
  ENTREGA_DEMORADA: { texto: 'Entrega demorada', color: 'bg-red-100 text-red-900' },
};

export function Operacion() {
  const conexion = useConexion();
  const operacion = useOperacion(conexion === 'conectado');
  const [soloAlertas, setSoloAlertas] = useState(false);
  const [tipo, setTipo] = useState<OrderType | 'TODOS'>('TODOS');
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);

  useOperacionEnVivo(conexion);

  if (operacion.isLoading) return <Cargando texto="Cargando la operación…" />;
  if (operacion.isError) {
    return (
      <ErrorCarga
        mensaje={getErrorMessage(operacion.error)}
        onReintentar={() => void operacion.refetch()}
      />
    );
  }

  const { pedidos, resumen } = operacion.data!;
  const conAlerta = pedidos.filter((p) => p.alertas.length > 0);

  /**
   * El tipo se filtra aparte de la búsqueda de texto.
   *
   * Domicilios y encomiendas comparten tablero porque comparten la operación,
   * pero quien está resolviendo un atasco suele ir a por uno de los dos. Un
   * filtro explícito evita tener que escribir «encomienda» y confiar en que
   * alguna columna contenga esa palabra.
   */
  const porTipo =
    tipo === 'TODOS' ? pedidos : pedidos.filter((p) => p.type === tipo);
  const base = soloAlertas ? porTipo.filter((p) => p.alertas.length > 0) : porTipo;

  const filtrados = base.filter((p) =>
    coincide(
      busqueda,
      p.code,
      p.merchantName ?? p.pickupName,
      p.courierName,
      p.deliveryAddress.addressLine,
      p.deliveryAddress.neighborhood,
      // También por el estado escrito: «demorada» o «preparación» son
      // términos que alguien teclea sin pensar que son un filtro.
      orderStatusLabel(p.status, p.type),
      p.type === OrderType.PARCEL ? 'encomienda' : 'domicilio',
    ),
  );

  const paginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
  // Si el filtro reduce la lista, la página actual puede haber dejado de
  // existir: se muestra la última en vez de una pantalla vacía sin motivo.
  const paginaActual = Math.min(pagina, paginas);
  const visibles = filtrados.slice((paginaActual - 1) * POR_PAGINA, paginaActual * POR_PAGINA);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl text-kick-dark">Operación</h1>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-kick-gray">
            Qué está pasando ahora mismo <span aria-hidden>·</span>
            <IndicadorConexion estado={conexion} />
          </p>
        </div>
        {conAlerta.length > 0 && (
          <Boton
            variante={soloAlertas ? 'primario' : 'secundario'}
            icono={<TriangleAlert className="h-4 w-4" />}
            onClick={() => setSoloAlertas((v) => !v)}
          >
            {conAlerta.length} con alertas
          </Boton>
        )}
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metrica
          icono={<ClipboardList className="h-5 w-5" />}
          etiqueta="Pedidos en curso"
          valor={String(resumen.pedidos.activos)}
          detalle={`${resumen.pedidos.sinDomiciliario} sin domiciliario`}
          alerta={resumen.pedidos.sinDomiciliario > 0}
        />
        <Metrica
          icono={<Bike className="h-5 w-5" />}
          etiqueta="Flota en turno"
          valor={String(resumen.flota.enTurno)}
          detalle={`${resumen.flota.libres} libres · ${resumen.flota.ocupados} ocupados`}
          alerta={resumen.flota.libres === 0 && resumen.pedidos.sinDomiciliario > 0}
        />
        <Metrica
          icono={<Store className="h-5 w-5" />}
          etiqueta="Comercios abiertos"
          valor={String(resumen.comercios.abiertos)}
          detalle={`de ${resumen.comercios.activos} activos`}
        />
        <Metrica
          icono={<Activity className="h-5 w-5" />}
          etiqueta="Hoy"
          valor={formatCOP(resumen.hoy.gmv)}
          detalle={`${resumen.hoy.entregados} entregados · ${resumen.hoy.cancelados} caídos`}
        />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-kick-gray"
          />
          <input
            type="search"
            value={busqueda}
            onChange={(e) => {
              setBusqueda(e.target.value);
              // Al cambiar el filtro se vuelve al principio: quedarse en la
              // página 4 de un resultado de dos filas no le sirve a nadie.
              setPagina(1);
            }}
            placeholder="Buscar por código, comercio, domiciliario o dirección"
            aria-label="Buscar pedidos"
            className="w-full rounded-kick border border-kick-gray/30 py-2.5 pl-9 pr-3 text-sm text-kick-ink outline-none transition focus:border-kick-red"
          />
        </div>
        <div className="flex rounded-kick border border-kick-gray/30 p-0.5" role="group" aria-label="Clase de encargo">
          {(
            [
              ['TODOS', 'Todos'],
              [OrderType.DELIVERY, 'Domicilios'],
              [OrderType.PARCEL, 'Encomiendas'],
            ] as const
          ).map(([valor, texto]) => (
            <button
              key={valor}
              onClick={() => {
                setTipo(valor);
                setPagina(1);
              }}
              aria-pressed={tipo === valor}
              className={cn(
                'rounded-kick px-3 py-2 text-sm font-semibold transition',
                tipo === valor ? 'bg-kick-red text-white' : 'text-kick-gray hover:text-kick-ink',
              )}
            >
              {texto}
            </button>
          ))}
        </div>

        <p className="text-sm text-kick-gray">
          {filtrados.length} {filtrados.length === 1 ? 'pedido' : 'pedidos'}
          {(busqueda.trim() || tipo !== 'TODOS') && ` de ${pedidos.length}`}
        </p>
      </div>

      {visibles.length === 0 ? (
        <div className="mt-4">
          <Vacio
            icono={<ClipboardList className="h-6 w-6" />}
            titulo={
              busqueda.trim()
                ? 'Ningún pedido coincide'
                : soloAlertas
                  ? 'Nada que atender'
                  : 'Ningún pedido en curso'
            }
            descripcion={
              busqueda.trim()
                ? `Nada con «${busqueda.trim()}». Prueba con el código o solo el nombre del comercio.`
                : soloAlertas
                  ? 'Todos los pedidos van dentro de lo esperado.'
                  : 'Cuando entre un pedido aparecerá aquí.'
            }
          />
        </div>
      ) : (
        <>
          <div className="mt-4 space-y-3">
            {visibles.map((pedido) => (
              <Fila key={pedido.id} pedido={pedido} />
            ))}
          </div>

          {paginas > 1 && (
            <div className="mt-4 flex items-center justify-between gap-3">
              <Boton
                variante="secundario"
                icono={<ChevronLeft className="h-4 w-4" />}
                disabled={paginaActual === 1}
                onClick={() => setPagina((p) => Math.max(1, p - 1))}
              >
                Anterior
              </Boton>

              <p className="text-sm text-kick-gray">
                Página {paginaActual} de {paginas}
              </p>

              <Boton
                variante="secundario"
                icono={<ChevronRight className="h-4 w-4" />}
                disabled={paginaActual === paginas}
                onClick={() => setPagina((p) => Math.min(paginas, p + 1))}
              >
                Siguiente
              </Boton>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Fila de pedido ──────────────────────────────────────────

function Fila({ pedido }: { pedido: PedidoOperacion }) {
  const puede = usePuede();
  const avisos = useAvisos();
  const { reasignar, reintentar, cancelar } = useIntervenirPedido();
  const disponibles = useDisponibles();

  const [reasignando, setReasignando] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [courierId, setCourierId] = useState('');
  const [motivo, setMotivo] = useState('');

  const urgente = pedido.alertas.length > 0;

  return (
    <article
      className={cn(
        'card-kick p-4',
        urgente && 'border-l-4 border-l-red-500',
      )}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono font-bold text-kick-dark">{pedido.code}</span>
            {/* La etiqueta solo aparece en la encomienda: el domicilio es el
                caso normal y marcarlo sería ruido en todas las filas. */}
            {pedido.type === OrderType.PARCEL && (
              <span className="flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-900">
                <Package className="h-3 w-3" />
                Encomienda
              </span>
            )}
            <span className="rounded-full bg-kick-light px-2 py-0.5 text-xs font-semibold text-kick-ink">
              {orderStatusLabel(pedido.status, pedido.type)}
            </span>
            {pedido.alertas.map((alerta) => (
              <span
                key={alerta}
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs font-semibold',
                  ALERTAS[alerta]?.color,
                )}
              >
                {ALERTAS[alerta]?.texto ?? alerta}
              </span>
            ))}
          </div>

          <p className="mt-1 text-sm text-kick-gray">
            {pedido.merchantName ?? pedido.pickupName} → {pedido.deliveryAddress.addressLine}
            {' · '}
            {formatDistance(pedido.distanceMeters)}
          </p>
          <p className="text-sm text-kick-gray">
            {pedido.courierName ? (
              <>
                <Bike className="mr-1 inline h-3.5 w-3.5" />
                {pedido.courierName}
              </>
            ) : (
              <span className="text-red-600">sin domiciliario asignado</span>
            )}
            {pedido.placedAt && ` · entró ${desdeHace(pedido.placedAt)}`}
          </p>
        </div>

        <span className="font-bold text-kick-dark">{formatCOP(pedido.total)}</span>
      </div>

      {puede('operacion', 'intervenir') && (
      <div className="mt-3 flex flex-wrap gap-2">
        {!pedido.courierId && (
          <Boton
            variante="secundario"
            className="px-3 py-1.5 text-xs"
            icono={<RefreshCw className="h-3.5 w-3.5" />}
            cargando={reintentar.isPending}
            onClick={() =>
              reintentar.mutate(pedido.id, {
                onSuccess: () => avisos.exito(`Buscando domiciliario para ${pedido.code}`),
                onError: (error) => avisos.error(getErrorMessage(error)),
              })
            }
          >
            Buscar de nuevo
          </Boton>
        )}

        <Boton
          variante="secundario"
          className="px-3 py-1.5 text-xs"
          icono={<Bike className="h-3.5 w-3.5" />}
          onClick={() => setReasignando(true)}
        >
          {pedido.courierId ? 'Reasignar' : 'Asignar a mano'}
        </Boton>

        <Boton
          variante="peligro"
          className="px-3 py-1.5 text-xs"
          icono={<Ban className="h-3.5 w-3.5" />}
          onClick={() => setCancelando(true)}
        >
          Cancelar
        </Boton>
      </div>
      )}

      <Modal
        abierto={reasignando}
        onCerrar={() => setReasignando(false)}
        titulo={`Reasignar ${pedido.code}`}
        descripcion="Solo aparecen los domiciliarios verificados que todavía tienen cupo para otro pedido."
        ancho="sm"
      >
        <div className="p-5">
          {disponibles.data?.length === 0 ? (
            <p className="rounded-kick bg-amber-50 px-3 py-2 text-sm text-amber-800">
              No hay ninguno libre en este momento.
            </p>
          ) : (
            <>
              <label
                htmlFor={`courier-${pedido.id}`}
                className="mb-1.5 block text-sm font-semibold text-kick-ink"
              >
                Domiciliario
              </label>
              <Seleccion
                id={`courier-${pedido.id}`}
                value={courierId}
                onChange={(e) => setCourierId(e.target.value)}
              >
                <option value="">Elige uno…</option>
                {disponibles.data?.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.fullName} · {Number(d.ratingAvg).toFixed(1)}★
                    {d.activeOrders > 0
                      ? ` · lleva ${d.activeOrders} pedido${d.activeOrders === 1 ? '' : 's'}`
                      : ' · libre'}
                  </option>
                ))}
              </Seleccion>

              <label
                htmlFor={`motivo-${pedido.id}`}
                className="mb-1.5 mt-4 block text-sm font-semibold text-kick-ink"
              >
                Motivo
              </label>
              <Entrada
                id={`motivo-${pedido.id}`}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="El domiciliario se accidentó"
                maxLength={300}
              />
              <p className="mt-1.5 text-xs text-kick-gray">
                Queda en la bitácora de auditoría junto con quién hizo el cambio.
              </p>
            </>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <Boton variante="secundario" onClick={() => setReasignando(false)}>
              Cancelar
            </Boton>
            <Boton
              disabled={!courierId || motivo.trim().length < 5}
              cargando={reasignar.isPending}
              onClick={() =>
                reasignar.mutate(
                  { id: pedido.id, courierId, reason: motivo.trim() },
                  {
                    onSuccess: () => {
                      setReasignando(false);
                      setMotivo('');
                      avisos.exito(`${pedido.code} reasignado`);
                    },
                    onError: (error) => avisos.error(getErrorMessage(error)),
                  },
                )
              }
            >
              Reasignar
            </Boton>
          </div>
        </div>
      </Modal>

      <Modal
        abierto={cancelando}
        onCerrar={() => setCancelando(false)}
        titulo={`Cancelar ${pedido.code}`}
        descripcion="El cliente y el comercio lo verán al instante. El motivo queda registrado."
        ancho="sm"
      >
        <div className="p-5">
          <label
            htmlFor={`cancel-${pedido.id}`}
            className="mb-1.5 block text-sm font-semibold text-kick-ink"
          >
            Motivo
          </label>
          <Entrada
            id={`cancel-${pedido.id}`}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="El comercio cerró sin avisar"
            maxLength={300}
          />
          <div className="mt-6 flex justify-end gap-2">
            <Boton variante="secundario" onClick={() => setCancelando(false)}>
              Volver
            </Boton>
            <Boton
              variante="peligro"
              disabled={motivo.trim().length < 5}
              cargando={cancelar.isPending}
              onClick={() =>
                cancelar.mutate(
                  { id: pedido.id, reason: motivo.trim() },
                  {
                    onSuccess: () => {
                      setCancelando(false);
                      setMotivo('');
                      avisos.exito(`${pedido.code} cancelado`);
                    },
                    onError: (error) => avisos.error(getErrorMessage(error)),
                  },
                )
              }
            >
              Cancelar pedido
            </Boton>
          </div>
        </div>
      </Modal>
    </article>
  );
}

// ── Piezas ──────────────────────────────────────────────────

/**
 * Refresca el monitor con los eventos del canal.
 *
 * Aun conectado se sigue consultando cada 30 s: las alertas dependen del
 * reloj, no de un evento. Un pedido cruza el umbral de «demorado» sin que
 * ocurra nada en el servidor, así que esperar un evento lo dejaría invisible.
 */
function useOperacionEnVivo(conexion: EstadoConexion): void {
  const qc = useQueryClient();
  const refrescar = useCallback(() => {
    void qc.invalidateQueries({ queryKey: clavesAdmin.operacion() });
  }, [qc]);

  useEventoSocket(WS_EVENT.ORDER_CREATED, refrescar);
  useEventoSocket(WS_EVENT.ORDER_STATUS_CHANGED, refrescar);
  useEventoSocket(WS_EVENT.COURIER_ASSIGNED, refrescar);
  useEventoSocket(WS_EVENT.ASSIGNMENT_FAILED, refrescar);

  useEffect(() => {
    if (conexion === 'conectado') refrescar();
  }, [conexion, refrescar]);
}

function IndicadorConexion({ estado }: { estado: EstadoConexion }) {
  if (estado === 'conectado') {
    return (
      <span className="inline-flex items-center gap-1.5 text-emerald-600">
        <Wifi className="h-3.5 w-3.5" />
        en vivo
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5',
        estado === 'conectando' ? 'text-amber-600' : 'text-red-600',
      )}
    >
      <WifiOff className="h-3.5 w-3.5" />
      {estado === 'conectando' ? 'reconectando…' : 'sin conexión'}
    </span>
  );
}

function Metrica({
  icono,
  etiqueta,
  valor,
  detalle,
  alerta,
}: {
  icono: React.ReactNode;
  etiqueta: string;
  valor: string;
  detalle: string;
  alerta?: boolean;
}) {
  return (
    <div className={cn('card-kick p-4', alerta && 'border border-red-200 bg-red-50')}>
      <div
        className={cn(
          'grid h-9 w-9 place-items-center rounded-kick',
          alerta ? 'bg-red-100 text-red-600' : 'bg-kick-light text-kick-gray',
        )}
      >
        {icono}
      </div>
      <p className="mt-3 text-2xl font-bold text-kick-dark">{valor}</p>
      <p className="text-sm font-semibold text-kick-ink">{etiqueta}</p>
      <p className="text-xs text-kick-gray">{detalle}</p>
    </div>
  );
}
